import type { FC } from 'react';
import React from 'react';
import {
  Button,
  Dropdown,
  FluidButtonLayout,
  Form,
  Gutter,
  Input,
  SectionHeader,
  ToastUtil,
  CondensedHeader,
  CondensedLarge,
} from '@axiom/ui';

import { AreaOfStudyApi } from '../../api/generated/area-of-study-api';
import {
  AreaOfStudyCategory,
  AreaOfStudyCategoryDisplay,
} from '../../enums/generated/area-of-study-category';
import type { AreaOfStudy } from '../../types/generated/area-of-study';

import { AdminModalAreasOfStudyFormSchema } from './admin-modal-areas-of-study-form-schema';

export type AdminModalAreasOfStudyFormProps = {
  name?: string;
  areasOfStudy: AreaOfStudy[];
  selected: Partial<AreaOfStudy>;
  onClose: () => void;
};

const categoryOptions = Object.values(AreaOfStudyCategory).map(value => ({
  key: value,
  value: AreaOfStudyCategoryDisplay[value],
}));

export const AdminModalAreasOfStudyForm: FC<
  AdminModalAreasOfStudyFormProps
> = ({ name, selected, areasOfStudy, onClose }) => {
  const isEditing = (selected.id?.length ?? 0) > 0;

  return (
    <Form
      name={name || 'AREAS_OF_STUDY_FORM'}
      initialValues={{
        name: selected.name,
        category: selected.category ?? undefined,
      }}
      schema={AdminModalAreasOfStudyFormSchema}
      onSubmit={async (_changedData, actions, values) => {
        const areaOfStudyValue = {
          ...values,
          isStem: selected.isStem ?? false,
        };

        const areaOfStudyExistsWithName = areasOfStudy.some(
          a =>
            a.name.toLowerCase() === areaOfStudyValue.name.toLowerCase() &&
            a.id !== selected.id
        );

        if (areaOfStudyExistsWithName) {
          ToastUtil.add({
            name: 'AREA_OF_STUDY_EXISTS',
            type: 'error',
            dismissible: true,
            children: (
              <>
                <CondensedHeader>Area of Study already exists</CondensedHeader>
                <CondensedLarge>
                  An Area of Study already exists with name &quot;
                  {areaOfStudyValue.name}&quot;
                </CondensedLarge>
              </>
            ),
          });
          return;
        }

        try {
          await (selected.id
            ? AreaOfStudyApi.updateAreaOfStudy({
                id: selected.id,
                body: areaOfStudyValue,
              })
            : AreaOfStudyApi.createAreasOfStudy({
                body: areaOfStudyValue,
              }));

          ToastUtil.add({
            name: 'AREA_OF_STUDY_SAVE_SUCCESS',
            type: 'info',
            dismissible: true,
            children: (
              <CondensedHeader>
                Area of study {isEditing ? 'updated' : 'created'} successfully
              </CondensedHeader>
            ),
          });

          onClose();
        } catch {
          ToastUtil.add({
            name: 'AREA_OF_STUDY_SAVE_ERROR',
            type: 'error',
            dismissible: true,
            children: (
              <>
                <CondensedHeader>Failed to save Area of Study</CondensedHeader>
                <CondensedLarge>Please try again later.</CondensedLarge>
              </>
            ),
          });
        }
      }}
    >
      {({ fireSubmit }) => (
        <>
          <Gutter bottom="16px" name="AREA_OF_STUDY_ADD_EDIT_FORM">
            <SectionHeader>
              {isEditing ? 'Edit' : 'Add'} Area of Study
            </SectionHeader>
          </Gutter>
          <Input name="name" label="Name" />
          <Gutter top="16px" />
          <Dropdown
            name="category"
            options={categoryOptions}
            label="Category"
            displayKey="value"
            valueKey="key"
          />
          <Gutter top="16px">
            <FluidButtonLayout>
              <Button
                name="CANCEL_BUTTON"
                onClick={() => {
                  onClose();
                }}
                variation="outline"
              >
                Cancel
              </Button>
              <Button name="SUBMIT_BUTTON" onClick={fireSubmit}>
                Save
              </Button>
            </FluidButtonLayout>
          </Gutter>
        </>
      )}
    </Form>
  );
};
